import { Fragment, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import ResizableContainer from "@visiblethread/react-components/dist/components/ResizableContainer";
import { useColumnsResizing } from "hooks/generic/useColumnsResizing";
import { ReactComponent as PlusIcon } from "icons/discovery/plus.svg";
import { ReactComponent as MinusIcon } from "icons/discovery/minus.svg";
import { ReactComponent as CaseSensitive } from "icons/dictionary/CaseSensitive.svg";
import { ReactComponent as Wildcard } from "icons/dictionary/Wildcard.svg";
import { ReactComponent as Regex } from "icons/dictionary/Regex.svg";
import { ReactComponent as OpenIcon } from "icons/discovery/expand.svg";
import { ReactComponent as CloseIcon } from "icons/discovery/collapse.svg";
import { ReactComponent as ErrorIcon } from "icons/dictionary/info-error.svg";
import Ellipsis from "components/Ellipsis";
import IconWithTooltip from "components/IconWithTooltip";
import { dictionaryTermTypes } from "constants/dictionary";
import {
  useScrollbarPlaceholderWidth,
  ScrollbarPlaceholder,
} from "hooks/generic/useScrollbarPlaceholderWidth";

const initColumnWidths = [234, 234];
const minimalColumnWidths = [180, 224];

const CategoriesTable = ({
  categories,
  selectedCategoryName,
  newlyCreatedCategoryName,
  resetNewlyCreatedCategoryName,
  categoriesErrors,
  onCategorySelect,
  onTermSelect,
  onEnter,
  onCategoryPick,
}) => {
  const headerRef = useRef(null);
  const bodyRef = useRef(null);
  const [openedCategories, setOpenedCategories] = useState(new Set());
  const [selectedTermIndex, setSelectedTermIndex] = useState(null);

  const { columnWidths, setColumnWidths, onColumnResize } = useColumnsResizing(
    initColumnWidths,
    minimalColumnWidths,
    "",
    headerRef
  );

  const { scrollbarPlaceholderWidth } = useScrollbarPlaceholderWidth(
    bodyRef,
    setColumnWidths
  );

  const handleBodyScroll = ({ target: { scrollLeft } }) => {
    if (headerRef.current && bodyRef.current) {
      headerRef.current.scrollLeft = scrollLeft;
    }
  };

  const handleKeyDown = ({ key }) => {
    if (key === "Enter" && selectedCategoryName) {
      onEnter();
    }
  };

  const handleOpenCloseAllCategories = () => {
    const newOpenedCategories = new Set([...openedCategories]);

    if (openedCategories.size === 0) {
      categories.forEach((category) => {
        newOpenedCategories.add(category.name);
      });
    } else {
      newOpenedCategories.clear();
    }

    setOpenedCategories(newOpenedCategories);
  };

  useEffect(() => {
    if (!newlyCreatedCategoryName) return;
    setSelectedTermIndex(null);
  }, [newlyCreatedCategoryName]);

  const getHeader = () => (
    <div ref={headerRef} className="categories_table__header">
      <ResizableContainer
        index={0}
        width={columnWidths[0]}
        minWidth={minimalColumnWidths[0]}
        onResize={onColumnResize}
        className="categories_table__header__cell"
      >
        <span>Terms</span>
        <button
          type="button"
          className="categories_table__header__cell__open_close_button"
          onClick={handleOpenCloseAllCategories}
        >
          {openedCategories.size === 0 ? <OpenIcon /> : <CloseIcon />}
          <div className="categories_table__header__cell__open_close_button__tooltip">
            {openedCategories.size === 0 ? "Expand all" : "Collapse all"}
          </div>
        </button>
      </ResizableContainer>
      <ResizableContainer
        index={1}
        width={columnWidths[1]}
        minWidth={minimalColumnWidths[1]}
        flexGrow={1}
        disableResizing
        onResize={onColumnResize}
        className="categories_table__header__cell"
      >
        Description
      </ResizableContainer>
      <ScrollbarPlaceholder width={scrollbarPlaceholderWidth} />
    </div>
  );

  const getTerms = (terms, categoryName) => {
    return terms.map(
      ({ name, description, caseSensitive, dictionaryTermType }, index) => {
        const handleTermClick = () => {
          setSelectedTermIndex(index);
          onCategorySelect(categoryName);
          onTermSelect(index, categoryName);
        };

        const handleDoubleClick = () => {
          onCategoryPick(categoryName, index);
        };

        return (
          <div
            role="presentation"
            key={name}
            className={clsx("categories_table__body__row", {
              selected:
                index === selectedTermIndex &&
                categoryName === selectedCategoryName,
            })}
            onClick={handleTermClick}
            onKeyDown={handleKeyDown}
            onDoubleClick={handleDoubleClick}
          >
            <div
              style={{ width: columnWidths[0] }}
              className="categories_table__body__row__cell term_row__name_cell"
            >
              <Ellipsis text={name} />
              {caseSensitive && (
                <IconWithTooltip
                  icon={<CaseSensitive />}
                  tooltipText="Case Sensitive"
                />
              )}
              {dictionaryTermType ===
                dictionaryTermTypes.WILDCARD_EXPRESSION && (
                <IconWithTooltip icon={<Wildcard />} tooltipText="Wildcard" />
              )}
              {dictionaryTermType ===
                dictionaryTermTypes.REGULAR_EXPRESSION && (
                <IconWithTooltip icon={<Regex />} tooltipText="Regex" />
              )}
            </div>
            <div
              style={{ width: columnWidths[1] }}
              className="categories_table__body__row__cell"
            >
              <Ellipsis text={description} />
            </div>
          </div>
        );
      }
    );
  };

  const getCategories = () => {
    return categories.map(({ name, description, terms }) => {
      const opened = openedCategories.has(name);
      const error = categoriesErrors.get(name);

      const handleCategoryClick = () => {
        setSelectedTermIndex(null);
        onCategorySelect(name);
      };

      const handleOpenCloseClick = (e) => {
        // Avoid category selection on open/close click
        e.stopPropagation();

        const newOpenedCategories = new Set([...openedCategories]);

        if (openedCategories.has(name)) {
          newOpenedCategories.delete(name);
        } else {
          newOpenedCategories.add(name);
        }

        setOpenedCategories(newOpenedCategories);
      };

      const handleDoubleClick = () => {
        onCategoryPick(name, null);
      };

      const scrollToCategory = (node) => {
        if (node !== null && name === newlyCreatedCategoryName) {
          node.scrollIntoView();
          resetNewlyCreatedCategoryName();
        }
      };

      return (
        <Fragment key={name}>
          <div
            role="presentation"
            ref={scrollToCategory}
            data-testid="category-row"
            className={clsx("categories_table__body__row category_row", {
              selected:
                name === selectedCategoryName && selectedTermIndex === null,
            })}
            onClick={handleCategoryClick}
            onKeyDown={handleKeyDown}
            onDoubleClick={handleDoubleClick}
          >
            <div
              style={{ width: columnWidths[0] }}
              className="categories_table__body__row__cell category_row__name_cell"
            >
              <div className="category_row__name_cell__name">
                {error && (
                  <IconWithTooltip
                    icon={<ErrorIcon width={16} height={16} />}
                    tooltipText={error}
                  />
                )}
                <Ellipsis text={name} />
              </div>
              <span className="category_row__name_cell__count">{`(${terms.length})`}</span>
              <button
                type="button"
                className="category_row__name_cell__open_close_button"
                onClick={handleOpenCloseClick}
              >
                {opened ? <MinusIcon /> : <PlusIcon />}
              </button>
            </div>
            <div
              style={{ width: columnWidths[1] }}
              className="categories_table__body__row__cell"
            >
              <Ellipsis text={description} />
            </div>
          </div>
          {opened && getTerms(terms, name)}
        </Fragment>
      );
    });
  };

  return (
    <div className="categories_table" data-testid="categories-table">
      {getHeader()}
      <div
        ref={bodyRef}
        className="categories_table__body"
        onScroll={handleBodyScroll}
      >
        {getCategories()}
      </div>
    </div>
  );
};

CategoriesTable.propTypes = {
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      description: PropTypes.string,
      terms: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string.isRequired,
          description: PropTypes.string,
        })
      ),
    })
  ).isRequired,
  selectedCategoryName: PropTypes.string.isRequired,
  newlyCreatedCategoryName: PropTypes.string,
  resetNewlyCreatedCategoryName: PropTypes.func,
  categoriesErrors: PropTypes.instanceOf(Map).isRequired,
  onCategorySelect: PropTypes.func.isRequired,
  onTermSelect: PropTypes.func.isRequired,
  onEnter: PropTypes.func.isRequired,
  onCategoryPick: PropTypes.func.isRequired,
};

export default CategoriesTable;
